import { useEffect, useMemo, useState } from "react";
import { cpFetch } from "../../lib/http";
import RoleMatrix from "../../components/RoleMatrix";
import i18n from "../../i18n";
import { decodeJwtPayload } from "../../lib/jwt";
import { useOptionalSharedServiceHealth } from "../../lib/ServiceHealthContext";
import { deriveServiceHealth } from "../../lib/serviceHealth";
import { presentViteEnvVars } from "./settingsDisplay";
import { SessionTokenField } from "./SessionTokenField";
import { ViteEnvPanel } from "./ViteEnvPanel";

type Me = { email?:string; name?:string; tenant?:string; roles?:string[]; lang?:string };

export function SettingsPage(){
  const auth = useMemo(() => JSON.parse(localStorage.getItem("dc.auth") || "null") || {}, []);
  const [me,setMe]=useState<Me|null>(null);
  const shared = useOptionalSharedServiceHealth();
  const status = shared?.payload ?? null;
  const health = shared?.health ?? deriveServiceHealth(null);
  const [smoke,setSmoke]=useState<any>(null);
  const [busy,setBusy]=useState(false);
  const [persist,setPersist]=useState<boolean>(!!localStorage.getItem("dc.persist"));
  const [tenant,setTenant]=useState<string>(auth.tenant || import.meta.env.VITE_TENANT_DEFAULT || "dev");
  const [appId,setAppId]=useState<string>(auth.app_id || auth.appId || localStorage.getItem("dc.app_id") || "");
  const [lang,setLang]=useState<string>(localStorage.getItem("dc.lang") || "en");
  const [flags,setFlags]=useState<{ dryRunOnly:boolean; hideDestructive:boolean }>({
    dryRunOnly: localStorage.getItem("dc.flag.dryrun")==="1",
    hideDestructive: localStorage.getItem("dc.flag.hidedestruct")==="1"
  });

  useEffect(()=> {
    (async ()=>{
      try { const m = await cpFetch<Me>("/me"); setMe(m); }
      catch {
        try {
          const p = decodeJwtPayload<any>(auth.token);
          setMe({ email:p?.email, name:p?.name, tenant:p?.tid||tenant, roles: (p?.roles||p?.role||p?.scope||"").toString().split(/[\,\s]+/).filter(Boolean), lang: p?.lang });
        } catch { setMe({ tenant, roles:[] }); }
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  function savePersist(on:boolean){ setPersist(on); if(on){ localStorage.setItem("dc.persist","1"); } else { localStorage.removeItem("dc.persist"); } }
  function saveTenant(t:string){ setTenant(t); localStorage.setItem("dc.auth", JSON.stringify({ ...auth, tenant: t, token: auth.token })); }
  function saveAppId(a:string){
    setAppId(a);
    localStorage.setItem("dc.app_id", a);
    localStorage.setItem("dc.auth", JSON.stringify({ ...auth, tenant, app_id: a, token: auth.token }));
  }
  function saveLang(l:string){ setLang(l); localStorage.setItem("dc.lang", l); (i18n as any).changeLanguage?.(l); }
  function saveFlag(k:"dryRunOnly"|"hideDestructive", v:boolean){ setFlags(s=>({ ...s, [k]: v })); const key = k==="dryRunOnly" ? "dc.flag.dryrun" : "dc.flag.hidedestruct"; if(v) localStorage.setItem(key,"1"); else localStorage.removeItem(key); }

  async function runSmoke(){ setBusy(true); setSmoke(null); try { const r = await cpFetch<any>("/ops/smoke", { method:"POST", body: JSON.stringify({ reason:"settings panel" }) }); setSmoke(r); } catch(e:any){ setSmoke({ error: e.message }); } finally { setBusy(false); } }

  const roles = me?.roles || [];
  const viteEnvRows = presentViteEnvVars({
    VITE_API_BASE: import.meta.env.VITE_API_BASE,
    VITE_CP_BASE: import.meta.env.VITE_CP_BASE,
    VITE_CDN_BASE: (import.meta as { env?: { VITE_CDN_BASE?: string } }).env?.VITE_CDN_BASE,
  });

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border p-4 space-y-3">
        <div className="text-sm text-gray-500">Profile</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <div className="text-xs text-gray-500">Email</div>
            <div>{me?.email || "—"}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Name</div>
            <div>{me?.name || "—"}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Tenant</div>
            <div className="flex gap-2">
              <input className="border rounded p-2 text-sm" value={tenant} onChange={e=>saveTenant(e.target.value)} aria-label="Default tenant"/>
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">App ID</div>
            <div className="flex gap-2">
              <input className="border rounded p-2 text-sm" value={appId} onChange={e=>saveAppId(e.target.value)} placeholder="e.g. app-1" aria-label="Default app id"/>
            </div>
          </div>
          <SessionTokenField
            token={typeof auth.token === "string" ? auth.token : ""}
            persist={persist}
            onPersistChange={savePersist}
          />
          <div>
            <div className="text-xs text-gray-500">Language</div>
            <select className="border rounded p-2 text-sm" value={lang} onChange={e=>saveLang(e.target.value)} aria-label="Language">
              <option value="en">English</option>
              <option value="af">Afrikaans</option>
            </select>
          </div>
          <div>
            <div className="text-xs text-gray-500">Roles</div>
            <div className="text-sm">{roles.join(", ") || "—"}</div>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border p-4 space-y-3">
        <div className="text-sm text-gray-500">Environment</div>
        <ViteEnvPanel rows={viteEnvRows} />
        <div className="mt-2">
          <div className="text-xs text-gray-500 mb-1">Backend status snapshot (same chrome /services/status)</div>
          <div className="text-sm font-medium mb-2">{health.label}{health.down > 0 ? ` · raw ok flag is not the pill` : ''}</div>
          {status
            ? <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-80">{JSON.stringify(status,null,2)}</pre>
            : <p className="text-xs text-gray-500">No shared snapshot yet.</p>}
        </div>
      </section>

      <section className="rounded-2xl border p-4 space-y-3">
        <div className="text-sm text-gray-500">Roles & Permissions</div>
        <RoleMatrix roles={roles}/>
      </section>

      <section className="rounded-2xl border p-4 space-y-2">
        <div className="text-sm text-gray-500">Guardrails (dev-only UI flags)</div>
        <label className="text-sm flex items-center gap-2">
          <input type="checkbox" checked={flags.dryRunOnly} onChange={e=>saveFlag("dryRunOnly", e.target.checked)} />
          Agent “dry-run only”
        </label>
        <label className="text-sm flex items-center gap-2">
          <input type="checkbox" checked={flags.hideDestructive} onChange={e=>saveFlag("hideDestructive", e.target.checked)} />
          Hide destructive ops (purge/stop) in UI
        </label>
        <div className="text-xs text-gray-500">These are local UI preferences; server policy still applies.</div>
      </section>

      <section className="rounded-2xl border p-4 space-y-2">
        <div className="text-sm text-gray-500">Run Smoke Test</div>
        <button className="rounded bg-slate-900 text-white px-4 py-2 disabled:opacity-50" onClick={runSmoke} disabled={busy}>
          {busy ? "Running…" : "Run Smoke Test"}
        </button>
        {smoke && (
          <div>
            <div className="text-xs text-gray-500 mt-2">Result</div>
            <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-80">{JSON.stringify(smoke,null,2)}</pre>
          </div>
        )}
      </section>
    </div>
  );
}

export default SettingsPage;
